/**
 * Zod schemas for organization-related entities
 * Provides runtime validation for API responses
 */

import { z } from 'zod';

import { agenticBusinessRoleSchema } from './agentic';

// ============================================================================
// MEMBERSHIP SCHEMAS
// ============================================================================

export const MembershipSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  email: z.string().email(),
  role: z.enum(['owner', 'admin', 'member']),
  /** Maps to org Agentic AI Configuration matrix; null/omitted = no profile assigned. */
  agentic_business_role: agenticBusinessRoleSchema.nullable().optional(),
  status: z.enum(['Active', 'Pending']),
  created_at: z.string().optional().nullable(),
});

export const MembershipArraySchema = z.array(MembershipSchema);

export type Membership = z.infer<typeof MembershipSchema>;

// ============================================================================
// ORGANIZATION SCHEMAS
// ============================================================================

export const OrganizationStatsSchema = z.object({
  total_active_users: z.number(),
  total_pro_seats: z.number(),
  total_unused_seats: z.number(),
});

export type OrganizationStats = z.infer<typeof OrganizationStatsSchema>;

export const OrganizationDataSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  role: z.string(),
  has_logo: z.boolean().optional(),
  status: z.enum(['pending', 'active', 'inactive']).optional().default('active'),
  enabled_industries: z.array(z.string()).optional().nullable(),
  enabled_ai_models: z.array(z.string()).optional().nullable(),
  thread_retention_days: z.number().optional().nullable(),
  file_retention_days: z.number().optional().nullable(),
  intake_completed_at: z.string().datetime().optional().nullable(),
  created_at: z.string().datetime().optional().nullable(),
  updated_at: z.string().datetime().optional().nullable(),
});

export type OrganizationData = z.infer<typeof OrganizationDataSchema>;

export const OrganizationIntakeSchema = z.object({
  organization_id: z.string(),
  company_name: z.string().nullable().optional(),
  company_size: z.enum(['1-10', '10-50', '50-100', '100+']).nullable().optional(),
  primary_industry: z.string().nullable().optional(),
  use_cases: z.array(z.string()).default([]),
  use_case_other: z.string().nullable().optional(),
  monthly_volume: z.string().nullable().optional(),
  document_types: z.array(z.string()).default([]),
  primary_language: z.enum(['english', 'french', 'spanish']).nullable().optional(),
  data_sensitivity: z.string().nullable().optional(),
  next_step_if_successful: z
    .enum(['paid-plan', 'extend-pilot', 'internal-rollout'])
    .nullable()
    .optional(),
  consent_anonymized_data_usage: z.boolean().nullable().optional(),
  completed_at: z.string().datetime().nullable().optional(),
});

export const OrganizationIntakePayloadSchema = z.object({
  company_name: z.string().min(1),
  company_size: z.enum(['1-10', '10-50', '50-100', '100+']),
  primary_industry: z.string(),
  use_cases: z.array(z.string()).min(1),
  use_case_other: z.string().optional(),
  monthly_volume: z.string().optional(),
  document_types: z.array(z.string()).default([]),
  primary_language: z.enum(['english', 'french', 'spanish']),
  data_sensitivity: z.string(),
  next_step_if_successful: z.enum(['paid-plan', 'extend-pilot', 'internal-rollout']),
  consent_anonymized_data_usage: z.boolean(),
});

export type OrganizationIntake = z.infer<typeof OrganizationIntakeSchema>;
export type OrganizationIntakePayload = z.infer<typeof OrganizationIntakePayloadSchema>;

// ============================================================================
// INVITATION SCHEMAS
// ============================================================================

export const InvitationSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  role: z.enum(['admin', 'member']),
  expires_at: z.string().datetime(),
  created_at: z.string().datetime().optional().nullable(),
});

export const InvitationArraySchema = z.array(InvitationSchema);

export type Invitation = z.infer<typeof InvitationSchema>;

// ============================================================================
// RETENTION SETTINGS SCHEMAS
// ============================================================================
// Backend only accepts thread_retention_days and file_retention_days (space/page
// retention was removed). Save requests omitting space_retention_days do not return 4xx.

export const RetentionSettingsSchema = z.object({
  thread_retention_days: z.number().nullable(),
  file_retention_days: z.number().nullable(),
});

export type RetentionSettings = z.infer<typeof RetentionSettingsSchema>;

// ============================================================================
// OUTPUT LAYOUT SETTINGS SCHEMAS
// ============================================================================

export const OrganizationOutputLogoSettingsSchema = z.object({
  position: z.enum(['left', 'center', 'right']),
  size: z.union([z.literal(0), z.literal(1), z.literal(2)]),
  file_name: z.string(),
  show_mark: z.boolean(),
  image_base64: z.string().nullable(),
  image_mime: z.string().nullable(),
});

export const OrganizationOutputSettingsSchema = z.object({
  logo: OrganizationOutputLogoSettingsSchema,
  brand_name: z.string().optional(),
  title: z.string().optional(),
  title_alignment: z.enum(['left', 'center', 'right']).optional(),
  subtitle: z.string().optional(),
  primary_color: z.string(),
  secondary_color: z.string(),
  footer: z.string(),
  page_numbers: z.boolean(),
  show_date: z.boolean(),
});

export type OrganizationOutputSettings = z.infer<typeof OrganizationOutputSettingsSchema>;

export const OrganizationOutputSettingsUpdateSchema = z.object({
  logo: OrganizationOutputLogoSettingsSchema.partial().optional(),
  title: z.string().optional(),
  title_alignment: z.enum(['left', 'center', 'right']).optional(),
  subtitle: z.string().optional(),
  primary_color: z.string().optional(),
  secondary_color: z.string().optional(),
  footer: z.string().optional(),
  page_numbers: z.boolean().optional(),
  show_date: z.boolean().optional(),
});

export type OrganizationOutputSettingsUpdate = z.infer<
  typeof OrganizationOutputSettingsUpdateSchema
>;

// ============================================================================
// GENERIC RESPONSE SCHEMAS
// ============================================================================

export const MessageResponseSchema = z.object({
  message: z.string().optional(),
  success: z.boolean().optional(),
});

export const ModelUpgradeResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

export type MessageResponse = z.infer<typeof MessageResponseSchema>;
export type ModelUpgradeResponse = z.infer<typeof ModelUpgradeResponseSchema>;

// ============================================================================
// VALIDATION HELPER FUNCTIONS
// ============================================================================

export const validateMembership = (data: unknown) => MembershipSchema.safeParse(data);
export const validateMembershipArray = (data: unknown) => MembershipArraySchema.safeParse(data);
export const validateOrganizationStats = (data: unknown) => OrganizationStatsSchema.safeParse(data);
export const validateOrganizationData = (data: unknown) => OrganizationDataSchema.safeParse(data);
export const validateOrganizationIntake = (data: unknown) =>
  OrganizationIntakeSchema.safeParse(data);
export const validateOrganizationIntakePayload = (data: unknown) =>
  OrganizationIntakePayloadSchema.safeParse(data);
export const validateInvitation = (data: unknown) => InvitationSchema.safeParse(data);
export const validateInvitationArray = (data: unknown) => InvitationArraySchema.safeParse(data);
export const validateRetentionSettings = (data: unknown) => RetentionSettingsSchema.safeParse(data);
export const validateOrganizationOutputSettings = (data: unknown) =>
  OrganizationOutputSettingsSchema.safeParse(data);
export const validateMessageResponse = (data: unknown) => MessageResponseSchema.safeParse(data);
export const validateModelUpgradeResponse = (data: unknown) =>
  ModelUpgradeResponseSchema.safeParse(data);
